import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, EmptyBox, KV } from "./shared";
import { LifecycleTabs } from "@/components/lifecycle/LifecycleTabs";
import { ArchivedPanel } from "@/components/lifecycle/ArchivedPanel";
import { ChevronRight, Loader2, Wand2, Archive, Brain, X } from "lucide-react";

export function Requests() {
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [lifeTab, setLifeTab] = useState("active");
  const [dnaFor, setDnaFor] = useState(null);   // request being viewed
  const [dna, setDna] = useState(null);
  const [dnaBusy, setDnaBusy] = useState(false);
  const load = () => api.get("/client-requests").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openDna = async (r) => {
    setDnaFor(r); setDna(null); setDnaBusy(true);
    try {
      const { data } = await api.get("/ai/studio/business-intelligence", { params: { email: r.email, generator: "copy" } });
      setDna(data);
    } catch (e) { toast.error(formatApiError(e)); setDnaFor(null); }
    finally { setDnaBusy(false); }
  };
  const sendReminder = async () => {
    try { const { data } = await api.post(`/client-requests/${dnaFor.id}/facts-reminder`); toast.success(`Reminder emailed to ${data.sent_to}`); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const archiveLead = async (r) => {
    if (!window.confirm(`Archive lead "${r.org_name}"? It moves to the Records Center where it can be restored or permanently deleted.`)) return;
    setBusyId(r.id);
    try { await api.post(`/lifecycle/leads/${r.id}/archive`, { reason: "" }); toast.success("Lead archived."); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };

  const genProposal = async (id, useAi) => {
    setBusyId(id);
    try {
      await api.post(`/proposals/from-request/${id}`, null, { params: { use_ai: useAi } });
      toast.success(useAi ? "AI proposal generated" : "Proposal generated");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };

  if (!items) return <Loader />;
  return (
    <div>
      <Head title="Assessment Requests" desc="Business Assessment submissions from prospective clients." />
      <div className="mb-5"><LifecycleTabs module="leads" value={lifeTab} onChange={setLifeTab} refreshKey={(items || []).length} /></div>
      {lifeTab !== "active" ? <ArchivedPanel module="leads" status={lifeTab} singular="Lead" onChanged={load} /> : (
      items.length === 0 ? <EmptyBox text="No requests yet." /> : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="bg-navy-800 border border-white/5 p-5" data-testid={`request-${r.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-white font-medium">{r.org_name}</div>
                  <div className="text-gray-500 text-sm">{r.contact_name} · {r.email} · {r.budget || "budget TBD"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-white/10 text-gray-300 px-3 py-1">{r.status}</span>
                  <button onClick={() => openDna(r)} data-testid={`view-dna-${r.id}`} className="text-gray-400 hover:text-gold text-sm flex items-center gap-1"><Brain size={14} /> View Business DNA</button>
                  <button onClick={() => setDetail(detail === r.id ? null : r.id)} className="text-gray-400 hover:text-gold text-sm flex items-center gap-1">Details <ChevronRight size={14} className={detail === r.id ? "rotate-90 transition-transform" : "transition-transform"} /></button>
                  <button onClick={() => archiveLead(r)} disabled={busyId === r.id} data-testid={`archive-lead-${r.id}`} className="text-gray-500 hover:text-red-400 text-sm flex items-center gap-1"><Archive size={14} /> Archive</button>
                </div>
              </div>
              {detail === r.id && (
                <div className="mt-4 pt-4 border-t border-white/10 grid md:grid-cols-2 gap-3 text-sm">
                  <KV k="Mission" v={r.mission} /><KV k="Vision" v={r.vision} />
                  <KV k="Audience" v={r.target_audience} /><KV k="Timeline" v={r.timeline} />
                  <KV k="Goals" v={(r.goals || []).join(", ")} /><KV k="Pages" v={(r.pages || []).join(", ")} />
                  <KV k="Features" v={(r.features || []).join(", ")} /><KV k="Colors" v={r.colors} />
                  <KV k="Visual style" v={(r.visual_style || []).join(", ")} /><KV k="Brand personality" v={(r.brand_personality || []).join(", ")} />
                  <KV k="Visual prefs" v={(r.visual_preferences || []).join(", ")} /><KV k="Design mood" v={(r.design_mood || []).join(", ")} />
                  <div className="md:col-span-2"><KV k="Desired feeling" v={r.design_feeling} /></div>
                  <KV k="Package" v={r.package_name} /><KV k="Design" v={r.selected_design} />
                  <div className="md:col-span-2"><KV k="Details" v={r.details} /></div>
                  <div className="md:col-span-2 flex gap-3 pt-2">
                    <button onClick={() => genProposal(r.id, false)} disabled={busyId === r.id} data-testid={`gen-proposal-${r.id}`} className="bg-gold text-navy px-5 py-2.5 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">Generate Proposal</button>
                    <button onClick={() => genProposal(r.id, true)} disabled={busyId === r.id} data-testid={`gen-ai-proposal-${r.id}`} className="border border-gold/40 text-gold px-5 py-2.5 text-sm hover:bg-gold/10 transition-colors flex items-center gap-2 disabled:opacity-60">{busyId === r.id ? <Loader2 className="animate-spin" size={14} /> : <Wand2 size={14} />} AI Proposal</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}

      {dnaFor && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4" onClick={() => setDnaFor(null)} data-testid="dna-modal">
          <div className="bg-navy border border-white/15 w-full max-w-lg max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-heading text-lg text-white flex items-center gap-2"><Brain size={18} className="text-gold" /> Business DNA — {dnaFor.org_name}</h4>
              <button onClick={() => setDnaFor(null)} aria-label="Close"><X size={18} className="text-gray-400 hover:text-white" /></button>
            </div>
            <p className="text-xs text-gray-500 mb-4">What we already know about this prospect — from their assessment, before they're even a project.</p>
            {dnaBusy ? <div className="py-10 text-center text-gray-400"><Loader2 className="animate-spin inline" size={20} /></div>
            : !dna?.loaded ? <div className="py-8 text-center text-gray-500 text-sm">No Business DNA found for this email yet.</div>
            : (() => {
                const strands = dna.knowledge?.dimensions || dna.dna?.strands || [];
                const cf = dna.fields?.advanced?.confirmed_facts?.value || {};
                const cfEntries = Object.entries(cf).filter(([, v]) => String(v || "").trim());
                return (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <span>Knowledge</span>
                      <div className="w-28 h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-gold" style={{ width: `${dna.knowledge?.score || 0}%` }} /></div>
                      <span className="text-gold font-heading">{dna.knowledge?.score || 0}%</span>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5" data-testid="dna-strands">
                      {strands.map((s) => (
                        <div key={s.key} className="flex items-center gap-2 text-sm">
                          <span className={s.filled || s.stars >= 1 ? "text-emerald-400" : "text-gray-600"}>{s.filled || s.stars >= 1 ? "●" : "○"}</span>
                          <span className={s.filled || s.stars >= 1 ? "text-gray-200" : "text-gray-500"}>{s.label}</span>
                        </div>
                      ))}
                    </div>
                    {cfEntries.length > 0 && (
                      <div className="border-t border-white/10 pt-3">
                        <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Confirmed business facts</div>
                        <div className="space-y-1" data-testid="dna-confirmed-facts">
                          {cfEntries.map(([k, v]) => (
                            <div key={k} className="text-sm"><span className="text-gray-500">{k.replace(/_/g, " ")}: </span><span className="text-gray-200">{v}</span></div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            {dna?.loaded && dnaFor.email && (
              <button onClick={sendReminder} data-testid="send-facts-reminder" className="mt-4 w-full text-sm border border-gold/40 text-gold px-3 py-2 hover:bg-gold/10 transition-colors">
                Email client to complete their trust facts
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
